import { FileText, ShieldCheck, Phone, Mail } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const forms = [
  { id: "DCY 01234", title: "Child Enrollment & Health Information", rule: "OAC 5101:2-12-15" },
  { id: "DCY 01236", title: "Medical/Physical Care Plan", rule: "Health care plan" },
  { id: "DCY 01217", title: "Request for Administration of Medication", rule: "Medication" },
  { id: "DCY 01250", title: "Plan of Operation for Child Care", rule: "Licensing operations" },
  { id: "DCY 01276", title: "Health Training Documentation", rule: "Staff training" },
  { id: "DCY 01299", title: "Incident / Injury Report", rule: "Incident reporting" },
  { id: "CCU-202", title: "Fire Drill Log", rule: "Emergency preparedness" },
  { id: "CCU-204", title: "Daily Sign-In Sheet", rule: "Attendance" },
];

const ratioRows = [
  { group: "Infant (< 12 mo)", ratio: "1 : 5", maxGroup: 12, bg: "bg-emerald-950/60" },
  { group: "Young Toddler (12–18 mo)", ratio: "1 : 6", maxGroup: 12, bg: "bg-emerald-950/60" },
  { group: "Older Toddler (18–30 mo)", ratio: "1 : 7", maxGroup: 14, bg: "bg-emerald-950/60" },
  { group: "Preschool (2.5–3 yrs)", ratio: "1 : 10", maxGroup: 20, bg: "bg-emerald-950/60" },
  { group: "Preschool (3–4 yrs)", ratio: "1 : 12", maxGroup: 24, bg: "bg-emerald-950/60" },
  { group: "Preschool (4–5 yrs)", ratio: "1 : 14", maxGroup: 28, bg: "bg-emerald-950/60" },
  { group: "School-age (5–11 yrs)", ratio: "1 : 18", maxGroup: 36, bg: "bg-emerald-950/60" },
];

export const OhioCompliance = () => {
  return (
    <section id="ohio-compliance" data-testid="ohio-compliance-section" className="relative py-24 sm:py-32 bg-emerald-950 text-stone-50 overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1654115480560-67642e776a30?crop=entropy&cs=srgb&fm=jpg&w=1600&q=70')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-950/95 via-emerald-950/90 to-emerald-900/95 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-900/60 border border-emerald-700/50 text-xs font-semibold uppercase tracking-[0.18em] text-emerald-200">
              <ShieldCheck className="w-3.5 h-3.5" />
              Ohio Compliance Module
            </div>
            <h2 className="mt-6 font-display text-4xl sm:text-5xl font-medium tracking-tight text-stone-50">
              Every form. Every ratio.<br />Audit-ready by default.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-emerald-100/70 max-w-md">
              ChildCheck is built around Ohio Administrative Code <span className="text-emerald-200 font-medium">5101:2-12</span> and
              the Department of Children and Youth (DCY). Forms come pre-filled, ratios update live,
              and you are never more than one click from a clean inspection.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="https://childrenandyouth.ohio.gov/forms"
                target="_blank"
                rel="noreferrer"
                data-testid="ohio-forms-link"
                className="inline-flex items-center gap-2 text-sm font-medium text-emerald-200 hover:text-white"
              >
                <FileText className="w-4 h-4" /> DCY forms library
                <i className="fa-solid fa-arrow-up-right-from-square text-[10px]"></i>
              </a>
              <a
                href="tel:18773022347"
                data-testid="ohio-help-link"
                className="inline-flex items-center gap-2 text-sm font-medium text-emerald-200 hover:text-white"
              >
                <Phone className="w-4 h-4" /> 1-877-302-2347
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            {/* Form cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {forms.map((f, i) => (
                <div
                  key={f.id}
                  data-testid={`ohio-form-card-${i}`}
                  className="backdrop-blur-lg bg-emerald-950/50 border border-emerald-800/50 rounded-xl p-4 hover:border-emerald-600/70 hover:-translate-y-0.5 transition-all"
                >
                  <div className="flex items-start justify-between">
                    <div className="w-8 h-8 rounded-lg bg-emerald-700/30 grid place-items-center">
                      <FileText className="w-4 h-4 text-emerald-300" />
                    </div>
                    <span className="text-[10px] font-bold text-emerald-400/80 tracking-wider">OHIO</span>
                  </div>
                  <div className="mt-3 font-display text-sm font-semibold text-emerald-100">{f.id}</div>
                  <div className="mt-1 text-[11px] text-emerald-200/60 leading-snug">{f.title}</div>
                </div>
              ))}
            </div>

            {/* Ratio table */}
            <div className="mt-8 rounded-2xl border border-emerald-800/50 bg-emerald-950/40 backdrop-blur-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-emerald-800/50 flex items-center justify-between">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-emerald-300">
                    OAC 5101:2-12-18 · Appendix A
                  </div>
                  <div className="font-display text-base mt-1 text-stone-50">Maximum Staff-to-Child Ratios</div>
                </div>
                <span className="hidden sm:inline text-[10px] px-2 py-1 rounded-md bg-emerald-800/50 text-emerald-200 font-semibold">
                  LIVE TRACKING
                </span>
              </div>
              <div className="overflow-x-auto">
                <Table data-testid="ohio-ratio-table">
                  <TableHeader>
                    <TableRow className="border-emerald-800/50 hover:bg-transparent">
                      <TableHead className="text-emerald-300 text-xs uppercase tracking-widest">Age Group</TableHead>
                      <TableHead className="text-emerald-300 text-xs uppercase tracking-widest">Ratio</TableHead>
                      <TableHead className="text-emerald-300 text-xs uppercase tracking-widest text-right">Max Group</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ratioRows.map((r) => (
                      <TableRow key={r.group} className="border-emerald-800/40 hover:bg-emerald-900/30">
                        <TableCell className="text-stone-100 text-sm">{r.group}</TableCell>
                        <TableCell className="text-stone-50 font-display text-base font-semibold">{r.ratio}</TableCell>
                        <TableCell className="text-emerald-200/80 text-sm text-right">{r.maxGroup}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="px-6 py-3 border-t border-emerald-800/50 flex items-center gap-2 text-[11px] text-emerald-200/60">
                <Mail className="w-3.5 h-3.5" /> Source: Ohio Admin. Code 5101:2-12-18 · childcarepolicy@childrenandyouth.ohio.gov
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OhioCompliance;
